import { SimilarTechnologySummaryDto } from '@@types/api.types';
import { ConfidenceScore } from '@components/shared/confidence-score.component';
import { ExpandableText } from '@components/shared/expandable-text.component';
import { ExternalLink } from '@components/shared/external-link.component';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import { Badge } from '@components/ui/badge';
import { cn } from '@lib/utils';
import React, { FC } from 'react';

export const getOverallSimilarTechnologiesConfidence = (summary: SimilarTechnologySummaryDto): number | null => {
  if (summary.matches.length === 0) return null;

  return Math.round(summary.matches.reduce((sum, match) => sum + match.confidence, 0) / summary.matches.length);
};

// Suggest-edits need the trigger's text to be selectable: when a select-drag
// ends inside the trigger, preventDefault makes Radix skip its toggle (it
// checks defaultPrevented), so only plain clicks expand/collapse.
const suppressToggleOnTextSelection = (event: React.MouseEvent<HTMLButtonElement>): void => {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed) return;

  const { anchorNode, focusNode } = selection;
  const endsInsideTrigger =
    (anchorNode && event.currentTarget.contains(anchorNode)) || (focusNode && event.currentTarget.contains(focusNode));

  if (endsInsideTrigger) {
    event.preventDefault();
  }
};

type SimilarTechnologiesSummaryContentProps = {
  summary: SimilarTechnologySummaryDto;
};

export const SimilarTechnologiesSummaryContent: FC<SimilarTechnologiesSummaryContentProps> = (props) => {
  const { summary } = props;

  return (
    <>
      {summary.summary && (
        <ExpandableText
          className="text-xs text-gray-600"
          maxLines={3}
          text={summary.summary}
        />
      )}

      <Accordion
        className="w-full space-y-2"
        type="multiple"
      >
        {summary.matches.map((tech, index) => {
          const key = `tech-${index}`;
          return (
            <AccordionItem
              className="rounded-lg border border-gray-200 bg-white"
              key={key}
              value={key}
            >
              <AccordionTrigger
                className="relative select-text p-3 hover:no-underline"
                onClick={suppressToggleOnTextSelection}
              >
                <ConfidenceScore
                  className="absolute left-3 top-3"
                  design="box"
                  score={tech.confidence}
                  size="xs"
                />
                <div className="flex w-full flex-col gap-1 pl-10 text-left">
                  <div className="flex items-start gap-2">
                    <h4 className="text-sm font-medium text-gray-600">{tech.name}</h4>
                    <div className="flex gap-1">
                      {tech.patentOrPaper && (
                        <Badge
                          className="border-blue-200 bg-blue-50 px-1.5 py-0.5 text-xs text-blue-700"
                          size="xs"
                          variant="secondary"
                        >
                          Patent/Paper
                        </Badge>
                      )}
                      {tech.inferred && (
                        <Badge
                          className="px-1.5 py-0.5 text-xs"
                          size="xs"
                          variant="outline"
                        >
                          Inferred
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>Category: {tech.category}</span>
                  </div>
                </div>
              </AccordionTrigger>

              <AccordionContent className="px-0 pt-0">
                <div className="flex flex-col gap-4 border-t border-gray-200">
                  <div className="mt-3 px-3">
                    <ExpandableText
                      className="text-xs text-gray-600"
                      maxLines={5}
                      text={tech.explanation}
                    />
                  </div>

                  {tech.products && tech.products.length > 0 && (
                    <>
                      <div className="border-t border-gray-200" />
                      <div className="px-3">
                        <div className="mb-2 text-sm font-medium text-gray-600">Products</div>
                        <ul className="flex flex-col gap-1">
                          {tech.products.map((pc, productIndex) => (
                            <li
                              className="text-xs font-light text-gray-600"
                              key={productIndex}
                            >
                              <span className="font-medium text-gray-600">• {pc.productName}</span>
                              <span className="text-gray-500"> by </span>
                              <span className="font-medium text-gray-600">{pc.companyName}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </>
                  )}

                  {tech.evidence && tech.evidence.length > 0 && (
                    <>
                      <div className="border-t border-gray-200" />
                      <div className="px-3 pb-2">
                        <div className="mb-2 text-sm font-medium text-gray-600">Evidence</div>
                        <div className="flex flex-col gap-2">
                          {tech.evidence.map((evidence, evidenceIndex) => (
                            <div
                              className="text-xs leading-relaxed text-gray-600"
                              key={evidenceIndex}
                            >
                              {evidence.description}{' '}
                              <ExternalLink
                                className="text-xs"
                                href={evidence.url}
                              >
                                Source
                              </ExternalLink>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      {summary.primaryReferences && summary.primaryReferences.length > 0 && (
        <div className="border-t border-gray-200 pt-3">
          <h4 className="mb-2 text-sm font-medium text-gray-600">Primary References</h4>
          <div className="flex flex-col gap-2">
            {summary.primaryReferences.map((ref, index) => (
              <div
                className="flex items-start gap-2"
                key={index}
              >
                <Badge
                  className={cn(
                    'px-1.5 py-0.5 text-xs capitalize',
                    ref.type === 'patent'
                      ? 'border-purple-200 bg-purple-50 text-purple-700'
                      : 'border-green-200 bg-green-50 text-green-700',
                  )}
                  size="xs"
                  variant="outline"
                >
                  {ref.type}
                </Badge>
                <div className="flex-1">
                  <div className="text-xs font-medium text-gray-600">{ref.title}</div>
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <span>{ref.source}</span>
                    <ExternalLink
                      className="text-xs"
                      href={ref.url}
                    >
                      View
                    </ExternalLink>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};
